import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  Checkbox,
  FormControlLabel,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  Grid,
  Paper,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import CommonModal from "../../components/common/CommonModal";
import { UserDto } from "../../hooks/useApplicationUser";
import { useTranslation } from "react-i18next";
import { useError } from "../../contexts/ErrorContext";
import {
  validateUserForm,
  FieldValidation,
  validateUsername,
} from "../../utils/validation";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

interface ApplicationUserModalProps {
  open: boolean;
  handleClose: () => void;
  initialData?: UserDto;
  onSubmit: (formData: UserDto) => void;
  isEdit: boolean;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

// Available roles for application users
const availableRoles = ["APP_USER"];

const ApplicationUserModal: React.FC<ApplicationUserModalProps> = ({
  open,
  handleClose,
  initialData,
  onSubmit,
  isEdit,
}) => {
  const [formData, setFormData] = useState<UserDto>({
    username: "",
    password: "",
    email: "",
    roles: ["APP_USER"], // Default role for application users
    status: true, // Boolean value
  });

  const { t } = useTranslation();
  const { showValidationError } = useError();

  const [errors, setErrors] = useState<FieldValidation>({});
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        password: isEdit ? "" : "", // Don't show existing password in edit mode
        roles:
          initialData.roles && initialData.roles.length > 0
            ? initialData.roles
            : ["APP_USER"], // Default role if empty
        username: initialData.username || initialData.email, // Use username or fallback to email
        status: typeof initialData.status === "string" 
          ? initialData.status === "Active" 
          : initialData.status, // Convert string to boolean if needed
      });
    } else {
      // Reset form when adding a new user
      setFormData({
        username: "",
        password: "",
        email: "",
        roles: ["APP_USER"], // Default role for application users
        status: true, // Boolean value
      });
    }
    setErrors({}); // Clear all old errors
  }, [initialData, open, isEdit]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "username") {
      // Allow letters, numbers, and underscores for username
      let newValue = value.replace(/[^A-Za-z0-9_]/g, "");
      setFormData((prev) => ({ ...prev, username: newValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleRoleChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      roles: typeof value === "string" ? value.split(",") : value,
    }));

    if (errors.roles) {
      setErrors((prev) => ({ ...prev, roles: null }));
    }
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ 
      ...prev, 
      status: e.target.checked 
    }));
  };

  const handleBlur = (fieldName: string) => {
    const validationErrors = validateUserForm(formData, isEdit);
    setErrors((prev) => ({
      ...prev,
      [fieldName]: validationErrors[fieldName] || null,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors = validateUserForm(formData, isEdit);
    setErrors(newErrors);

    // Show validation errors in toast if any
    const hasErrors = Object.values(newErrors).some((error) => error !== null);
    if (hasErrors) {
      // Convert nulls to empty strings for showValidationError
      const errorStrings: Record<string, string> = {};
      Object.keys(newErrors).forEach((key) => {
        errorStrings[key] = newErrors[key] || "";
      });
      showValidationError(errorStrings);
    }

    return !hasErrors;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const payload = { 
        ...formData, 
        status: Boolean(formData.status) // Ensure status is always boolean
      };
      // Remove password field when editing since it's not shown
      if (isEdit) {
        delete payload.password;
      }
      onSubmit(payload);
    }
  };

  const requiredFields = [
    "email",
    "username",
    ...(isEdit ? [] : ["password"]), // password required only when creating
    "roles",
  ];

  const isFormValid =
    requiredFields.every((field) => {
      const value = (formData as Record<string, any>)[field];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value && value.toString().trim() !== "";
    }) && 
    Object.values(errors).every((err) => !err);

  return (
    <CommonModal
      open={open}
      onClose={handleClose}
      title={isEdit ? t("edit_application_user") : t("add_new_application_user")}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      customActions={
        <>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!isFormValid}
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
          >
            {isEdit ? t("update") : t("create")}
          </Button>
        </>
      }
    >
        <Grid container spacing={2} sx={{ my: 2 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("username")}
              name="username"
              value={formData.username}
              onChange={handleChange}
              onBlur={() => handleBlur("username")}
              fullWidth
              error={!!errors.username}
              helperText={errors.username}
              margin="dense"
              size="small"
              required
              InputLabelProps={{
                sx: {
                  "& .MuiFormLabel-asterisk": {
                    color: "red",
                  },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label={t("email")}
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={() => handleBlur("email")}
              fullWidth
              error={!!errors.email}
              helperText={errors.email}
              margin="dense"
              size="small"
              required
              InputLabelProps={{
                sx: {
                  "& .MuiFormLabel-asterisk": {
                    color: "red",
                  },
                },
              }}
            />
          </Grid>
          {!isEdit && (
            <Grid item xs={12} md={6}>
              <TextField
                label={t("password")}
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                onBlur={() => handleBlur("password")}
                fullWidth
                error={!!errors.password}
                helperText={errors.password}
                margin="dense"
                size="small"
                required
                InputLabelProps={{
                  sx: {
                    "& .MuiFormLabel-asterisk": {
                      color: "red",
                    },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((show) => !show)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              margin="dense"
              error={!!errors.roles}
              required
              variant="outlined"
              size="small"
              sx={{
                backgroundColor: "#f8f9fb",
              }}
            >
              <InputLabel
                sx={{
                  "& .MuiFormLabel-asterisk": { color: "red" },
                }}
              >
                {t("roles")}
              </InputLabel>
              <Select
                multiple
                value={formData.roles || []}
                onChange={handleRoleChange}
                onBlur={() => handleBlur("roles")}
                input={<OutlinedInput label={t("roles")} size="small" />}
                size="small"
                displayEmpty
                sx={{
                  minHeight: "40px",
                  display: "flex",
                  alignItems: "center",
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    paddingTop: "8px",
                    paddingBottom: "8px",
                  },
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                }}
                renderValue={(selected) =>
                  (selected as string[]).length === 0 ? (
                    <span style={{ color: "#aaa" }}></span>
                  ) : (
                    <span style={{ color: "black" }}>
                      {(selected as string[]).join(", ")}
                    </span>
                  )
                }
                MenuProps={MenuProps}
              >
                {availableRoles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.roles}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(formData.status)}
                  onChange={handleStatusChange}
                  name="status"
                />
              }
              label={
                <span>
                  {t("status")}
                  <span style={{ color: "red" }}> *</span>
                </span>
              }
              sx={{ mt: 1.5 }}
            />
          </Grid>
        </Grid>
    </CommonModal>
  );
};

export default ApplicationUserModal;

